import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Dropdown, Counter} from '@/components/atoms';
import TimePickerInput from '@/components/atoms/TimePickerInput';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {
  gameActivitySetupJsonSchema,
  gameActivitySetupUiSchema,
  gameAvailabilityJsonSchema,
  gameAvailabilityUiSchema
} from '@/services/schema/activity';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {useGameActivitySetup} from '@/hooks/useGameActivitySetup';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import styles from './GameActivitySetUpBase.module.scss';

const GameActivitySetUpBase = ({
  activitySelection,
  formData,
  handleFormChange,
  activityJsonSchema = gameActivitySetupJsonSchema,
  activityUiSchemaFn = gameActivitySetupUiSchema,
  testId = 'game-activity-card'
}) => {
  const {dropdownList: daysOfWeekList} = useDropdown({
    dropdownType: DROPDOWN_TYPES.DaysOfWeek
  });

  const {
    handleDailyDisplayStartTimeChange,
    handleDailyDisplayEndTimeChange,
    minEndTime,
    handleDaysUnavailableChange,
    handleDailyPlayLimitChange,
    handleDailyPlayLimitKeyDown,
    daysUnavailableValue
  } = useGameActivitySetup({formData, handleFormChange, daysOfWeekList});

  const activitySetupUi = useMemo(
    () => activityUiSchemaFn(formData),
    [formData, activityUiSchemaFn]
  );

  const availabilityUi = useMemo(
    () =>
      gameAvailabilityUiSchema(
        formData?.startDate || '',
        formData?.endDate,
        formData,
        'top-left'
      ),
    [formData]
  );

  return (
    <div className='flex w-full flex-col gap-8' data-testid={testId}>
      {/* Activity Setup */}
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Activity Setup</h3>
        <div className={styles.setupRow}>
          <div className={styles.activityTypeWrap}>{activitySelection}</div>
          <div className={styles.fieldsWrap}>
            <JsonFormsWrapper
              jsonschema={activityJsonSchema}
              uischema={activitySetupUi}
              data={formData}
              onChange={handleFormChange}
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Availability</h3>
        <div className={styles.availabilityRowWrap}>
          <JsonFormsWrapper
            jsonschema={gameAvailabilityJsonSchema}
            uischema={availabilityUi}
            data={formData}
            onChange={handleFormChange}
          />
        </div>
        <div className={styles.timeDisplayRow}>
          <div className={styles.displayFirstRow}>
            <TimePickerInput
              label='Daily Display Start Time'
              value={formData?.dailyDisplayStartTime || ''}
              onChange={handleDailyDisplayStartTimeChange}
              id='daily-start-time'
              showLabelIcon={true}
              tooltipText='Leave empty for no time restriction'
            />
          </div>
          <div className={styles.displayFirstRow}>
            <TimePickerInput
              label='Daily Display End Time'
              value={formData?.dailyDisplayEndTime || ''}
              onChange={handleDailyDisplayEndTimeChange}
              id='daily-end-time'
              minTime={minEndTime}
              maxTime='23:59'
            />
          </div>
          <div className={styles.displayFirstRow}>
            <Dropdown
              label='Days of the Week Unavailable'
              isDark
              childList={daysOfWeekList}
              value={daysUnavailableValue}
              updateValue={handleDaysUnavailableChange}
              placeholder='Select days'
              isJsonWrap={false}
              selection='multiple'
              showLabelIcon={false}
              hideMenuListOnSelection={false}
            />
          </div>
        </div>
        <div className={styles.displayFirstRow}>
          <div className='flex flex-col gap-2'>
            <LabelWithTooltip
              className='text-[12px] text-[#d0d0d0]'
              label='Daily Play Limit'
            />
            <div onKeyDownCapture={handleDailyPlayLimitKeyDown}>
              <Counter
                feedbackType='info'
                value={
                  formData?.dailyPlayLimit === '' ||
                  formData?.dailyPlayLimit === null ||
                  formData?.dailyPlayLimit === undefined
                    ? 1
                    : formData?.dailyPlayLimit
                }
                onUpdate={handleDailyPlayLimitChange}
                minValue={1}
                maxValue={99}
                size='medium'
                className='w-20'
                style={{width: '76px'}}
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

GameActivitySetUpBase.propTypes = {
  activitySelection: PropTypes.node,
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  activityJsonSchema: PropTypes.object,
  activityUiSchemaFn: PropTypes.func,
  testId: PropTypes.string
};

export default React.memo(GameActivitySetUpBase);
